import Cognito from '@letsdothis/cognito';
import Cookies from 'universal-cookie';
import jwtDecode from 'jwt-decode';
import get from 'lodash/get';
import once from 'lodash/once';

import { ExperimentName } from '@letsdothis/experiments';
import { digestCookies } from '@letsdothis/quarantine';

import getUrl from '@/routes/routes/helpers/getUrl';
import { getObjFromHashParamString } from '@/utils/urls';
import { logError } from '@/utils/logError';
import { initAuth } from './cognitoAsync';
import {
  MAX_REFRESH_TIME,
  COGNITO_LAST_RETURN_URL,
  USER_COOKIE_NAME as LEGACY_USER_COOKIE_NAME,
} from './constants';
import { isSuperUserFromJWT } from './utils';

import oldAuth from './auth';

let cookies = new Cookies();
let idToken = null;
let primaryIdentityId = null;

const authorizeCallbackUrl = `${WEB_ROOT}${COGNITO_CALLBACK_PATH}`;

const client = new Cognito.Client({
  authorizeCallbackUrl,
  cognitoBaseUrl: COGNITO_BASE_URL,
  cognitoClientId: COGNITO_CLIENT_ID,
  graphQLRoot: GQL_ROOT,
});

const UNSAFE_mutateVariables = once(async () => {
  if (client.isLoggedIn()) {
    idToken = await client.getIdToken();
    primaryIdentityId = await client.getPrimaryIdentityId();
  }
});

// this is to ensure the client has access to the id token and primary ID
// once we refactor the whole auth in the webapp we'll remove this
if (!__SERVER__) {
  UNSAFE_mutateVariables();
}
/**
 * Temporary function to check which experiment is used
 */
const shouldUseLegacyAuth = () => {
  const experimentsFromCookies = digestCookies(cookies.getAll());
  if (experimentsFromCookies[ExperimentName.LDT_LOGIN] === 'variant') {
    return false;
  }
  return true;
};

const redirectToLegacyAuthWithStrava = () => {
  if (__CLIENT__) {
    const currentUrl = window.location.href;
    const hash = currentUrl.split('#')[1];
    const hashObject = hash ? getObjFromHashParamString(hash) : {};
    const query = {
      with: 'strava',
      exp_disable: ExperimentName.LDT_LOGIN,
    };
    const url = getUrl.loginPage({
      query,
      hash: hashObject,
    });
    window.location.href = url;
  }
};

const redirectToLegacyAuthIfStrava = (isLoginExperimentVariant) => {
  if (__CLIENT__) {
    const legacyPrimaryIdentityId = cookies.get(LEGACY_USER_COOKIE_NAME);
    if (legacyPrimaryIdentityId && legacyPrimaryIdentityId.includes('strava')) {
      redirectToLegacyAuthWithStrava();
    }
  }
};

// this function will mutate the cookies per request
// horrible, but the lesser evil for now
const setCookiesDomain = (domain) => {
  client.setCookiesDomain(domain);
};
const plugInCookies = async (universalCookieObject) => {
  cookies = universalCookieObject;
  if (shouldUseLegacyAuth()) return;
  client.setCookies(cookies);
  await UNSAFE_mutateVariables();
  return cookies;
};

const getUserCookie = () => {
  if (shouldUseLegacyAuth()) {
    return oldAuth.getUserCookie();
  }
  return primaryIdentityId;
};

const getPrimaryIdentityId = () => {
  if (shouldUseLegacyAuth()) {
    return oldAuth.getUserCookie();
  }
  return primaryIdentityId;
};

const getAuthCookie = () => {
  if (shouldUseLegacyAuth()) {
    return oldAuth.getAuthCookie();
  }
  return idToken;
};

const getIdToken = () => {
  if (shouldUseLegacyAuth()) {
    return oldAuth.getAuthCookie();
  }
  return idToken;
};

const updateIdToken = (newIdToken) => {
  idToken = newIdToken;
};

const updatePrimaryIdentityId = (newIdToken) => {
  idToken = newIdToken;
};

const setCookie = (name, value) => {
  cookies.set(name, value, {
    domain: get(window, 'location.hostname', ''),
    expires: new Date(new Date().getTime() + MAX_REFRESH_TIME),
    path: '/', // Required to be used site-wide
    secure: !__DEVELOPMENT__,
  });
};

const login = async (auth) => {
  if (shouldUseLegacyAuth()) {
    return oldAuth.login(auth);
  }
  const { provider, returnUrl } = auth;
  if (!provider) return;
  setCookie(COGNITO_LAST_RETURN_URL, returnUrl);
  await client.loginProvider(provider);
};

const loginEmailPassword = async ({ returnUrl, email, password }) => {
  if (!email || !password) return;
  setCookie(COGNITO_LAST_RETURN_URL, returnUrl);
  await client.loginUsernamePassword({
    username: email,
    password,
  });
};

const signUpEmailPassword = async ({
  email,
  givenName,
  familyName,
  password,
}) => {
  if (!email || !password || !givenName || !familyName) return;
  await client.signUpUsernamePassword({
    username: email,
    password,
    email,
    givenName,
    familyName,
  });
};

const recoverPassword = async ({ email }) => {
  await client.recoverPassword({ username: email });
};

const getCookie = (name) => cookies.get(name);

const removeCookie = (name) => {
  cookies.remove(name, {
    domain: get(window, 'location.hostname', ''),
    path: '/', // Required to be used site-wide
  });
};

const popCookie = (name) => {
  const value = getCookie(name);
  removeCookie(name);
  return value;
};
const getReturnUrl = () => {
  return popCookie(COGNITO_LAST_RETURN_URL) ?? '/';
};

const handleCallback = async (code) => {
  await client.handleCallback(code);
  idToken = await client.getIdToken();
  primaryIdentityId = await client.getPrimaryIdentityId();
};

const logout = (manual) => {
  if (oldAuth.loggedIn()) {
    oldAuth.logout(manual);
  }

  if (client.isLoggedIn()) {
    client.logout();
  }

  idToken = null;
  primaryIdentityId = null;
};

const hasUserCookie = () => {
  if (shouldUseLegacyAuth()) {
    return oldAuth.hasUserCookie();
  }
  return client.isLoggedIn();
};

const loggedIn = () => {
  if (shouldUseLegacyAuth()) {
    return oldAuth.loggedIn();
  }
  return client.isLoggedIn();
};

const authenticateRoute = (props, replace) => {
  if (shouldUseLegacyAuth()) {
    return oldAuth.authenticateRoute(props, replace);
  }
  const {
    location: { pathname, hash, search },
  } = props;
  if (!client.isLoggedIn()) {
    replace({
      pathname: '/login',
      query: { returnUrl: `${pathname}${search}${hash}` },
    });
  }
};

/**
 * Handler called when entering admin routes from Router
 * Decodes the auth token and checks that the user roles contain 'admin'.
 * Redirects to logout if the user is not an admin.
 * Copies old functionality
 */
const adminRoute = (props, replace) => {
  if (shouldUseLegacyAuth()) {
    return oldAuth.adminRoute(props, replace);
  }
  let admin = false;
  try {
    const authCookie = getAuthCookie();
    const user = jwtDecode(authCookie);
    // User roles includes admin
    if (user && isSuperUserFromJWT(user)) {
      admin = true;
    }
  } catch (error) {
    // JWT could not be decoded, or cookie doesn't exist
    logError(error);
  }
  if (!admin) {
    // TODO: This should probably 404 with no sign that there is an admin route here
    // To logout the user is intentionally annoying, but may also be annoying for dev/testing.
    replace({
      pathname: '/logout',
    });
  }
};

export default {
  setCookie,
  login,
  loginEmailPassword,
  signUpEmailPassword,
  recoverPassword,
  getCookie,
  removeCookie,
  popCookie,
  handleCallback,
  logout,
  hasUserCookie,
  loggedIn,
  authenticateRoute,
  adminRoute,
  plugInCookies,
  setCookiesDomain,
  getUserCookie,
  getPrimaryIdentityId,
  getAuthCookie,
  getIdToken,
  updateIdToken,
  updatePrimaryIdentityId,
  getReturnUrl,
  initAuth,
  redirectToLegacyAuthWithStrava,
  redirectToLegacyAuthIfStrava,
};
